import { useMemo, useState, type ReactNode } from 'react'
import { LogStats } from '../useLogStats'
import { Shield, Server, Gauge, Users, Search, ChevronRight, ChevronDown } from 'lucide-react'

interface ProxyRow {
  proxy: string
  port: string
  count: number
  cycles_sum: number
  /** Прокачек через этот адрес — знаменатель для «как часто капчит». */
  profiles: number
  /** Из них закончились капчей. */
  with_cap: number
  types: Record<string, number>
  difficulties: Record<string, number>
}

/** Один прокси-сервер: суммарно по всем его портам + сами порты. */
interface HostGroup {
  host: string
  count: number
  cycles_sum: number
  profiles: number
  with_cap: number
  types: Record<string, number>
  ports: ProxyRow[]
}

function splitProxy(proxy: string): { host: string; port: string } {
  const i = proxy.lastIndexOf(':')
  if (i <= 0) return { host: proxy, port: '' }
  return { host: proxy.slice(0, i), port: proxy.slice(i + 1) }
}

function mergeCounts(dst: Record<string, number>, src: Record<string, number>): void {
  for (const [k, v] of Object.entries(src || {})) {
    dst[k] = (dst[k] || 0) + (Number(v) || 0)
  }
}

function fmtAvg(sum: number, n: number, digits = 1): string {
  if (!n) return '—'
  const v = sum / n
  return Number.isInteger(v) ? String(v) : v.toFixed(digits)
}

function fmtPct(part: number, whole: number): string {
  if (!whole) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

function sortedEntries(obj: Record<string, number> | undefined): { name: string; count: number }[] {
  if (!obj) return []
  return Object.entries(obj)
    .map(([name, count]) => ({ name, count: Number(count) || 0 }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count)
}

function BarRow({ name, count, max, tone }: { name: string; count: number; max: number; tone: 'violet' | 'amber' | 'sky' }) {
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0
  const fill =
    tone === 'violet' ? 'bg-violet-500/80' :
    tone === 'amber' ? 'bg-amber-500/80' : 'bg-sky-500/80'
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_3.5rem] gap-2 items-center py-1">
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-foreground truncate font-medium">{name}</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
          <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-xs tabular-nums font-semibold text-right text-muted-foreground">{count.toLocaleString('ru-RU')}</span>
    </div>
  )
}

function TypeChips({ types }: { types: Record<string, number> }) {
  const rows = sortedEntries(types).slice(0, 4)
  if (!rows.length) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {rows.map(r => (
        <span
          key={r.name}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] bg-violet-500/10 text-violet-300 border border-violet-500/20"
          title={`${r.name}: ${r.count}`}
        >
          <span className="max-w-[9rem] truncate">{r.name}</span>
          <span className="tabular-nums font-semibold opacity-80">{r.count}</span>
        </span>
      ))}
    </div>
  )
}

/** Profiler-only captcha analytics — proxy / type / difficulty / cycles. */
export default function CaptchaDetailPanel({ stats }: { stats: LogStats }) {
  const [q, setQ] = useState('')
  const d = stats.profilerCaptchaDetail

  const solved = Number(d?.solved) || 0
  const cyclesSum = Number(d?.cycles_sum) || 0
  const profilesOk = Number(d?.profiles_ok) || Number(stats.profilerOkCount) || 0
  const profileCapSum = Number(d?.profile_cap_sum) || 0
  const profilesWithCap = Number(d?.profiles_with_cap) || 0

  // Бэкенд агрегирует разрез по СЕРВЕРУ, порты лежат внутри строки: ключом
  // раньше была пара host:port, и при тысячах портов провайдера потолок в 300
  // ключей вытеснял историю (в разрезе оставалось 2% капч). Старый формат
  // (ключ host:port, нет поля ports) поддерживается — данные из него
  // сворачиваются по серверу на лету.
  const hostGroups: HostGroup[] = useMemo(() => {
    const map = new Map<string, HostGroup>()
    const grab = (host: string): HostGroup => {
      let g = map.get(host)
      if (!g) {
        g = { host, count: 0, cycles_sum: 0, profiles: 0, with_cap: 0, types: {}, ports: [] }
        map.set(host, g)
      }
      return g
    }
    for (const [key, row] of Object.entries(d?.by_proxy || {}) as [string, any][]) {
      const ports = (row?.ports && typeof row.ports === 'object') ? row.ports : null
      const host = ports ? key : splitProxy(key).host
      const g = grab(host)
      g.count += Number(row?.count) || 0
      g.cycles_sum += Number(row?.cycles_sum) || 0
      g.profiles += Number(row?.profiles) || 0
      g.with_cap += Number(row?.with_cap) || 0
      mergeCounts(g.types, (row?.types && typeof row.types === 'object') ? row.types : {})
      if (ports) {
        for (const [port, p] of Object.entries(ports) as [string, any][]) {
          g.ports.push({
            proxy: `${host}:${port}`,
            port,
            count: Number(p?.count) || 0,
            cycles_sum: Number(p?.cycles_sum) || 0,
            profiles: Number(p?.profiles) || 0,
            with_cap: Number(p?.with_cap) || 0,
            types: {},
            difficulties: {},
          })
        }
      } else {
        g.ports.push({
          proxy: key,
          port: splitProxy(key).port,
          count: Number(row?.count) || 0,
          cycles_sum: Number(row?.cycles_sum) || 0,
          profiles: Number(row?.profiles) || 0,
          with_cap: Number(row?.with_cap) || 0,
          types: (row?.types && typeof row.types === 'object') ? row.types : {},
          difficulties: (row?.difficulties && typeof row.difficulties === 'object') ? row.difficulties : {},
        })
      }
    }
    const out = Array.from(map.values())
    out.forEach(g => g.ports.sort((a, b) => b.count - a.count))
    // Сверху — те, кто капчит чаще. Серверы с малым объёмом уходят вниз:
    // при двух прокачках доля 100% ничего не значит и только мешает читать.
    const MIN = 20
    const rate = (g: HostGroup) => (g.profiles ? g.with_cap / g.profiles : -1)
    return out.sort((a, b) => {
      const aOk = a.profiles >= MIN, bOk = b.profiles >= MIN
      if (aOk !== bOk) return aOk ? -1 : 1
      const dr = rate(b) - rate(a)
      if (Math.abs(dr) > 0.001) return dr
      return b.count - a.count
    })
  }, [d?.by_proxy])

  // Поиск работает и по адресу, и по порту: совпал порт — показываем его сервер
  // только с подходящими портами (и раскрываем, чтобы совпадение было видно).
  const visibleGroups = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return hostGroups
    const out: HostGroup[] = []
    for (const g of hostGroups) {
      if (g.host.toLowerCase().includes(needle)) {
        out.push(g)
        continue
      }
      const ports = g.ports.filter(p => p.proxy.toLowerCase().includes(needle))
      if (ports.length) {
        const types: Record<string, number> = {}
        ports.forEach(p => mergeCounts(types, p.types))
        out.push({
          host: g.host,
          count: ports.reduce((s, p) => s + p.count, 0),
          cycles_sum: ports.reduce((s, p) => s + p.cycles_sum, 0),
          profiles: ports.reduce((s, p) => s + p.profiles, 0),
          with_cap: ports.reduce((s, p) => s + p.with_cap, 0),
          types,
          ports,
        })
      }
    }
    return out
  }, [hostGroups, q])

  // Разрезы по личности профиля. Главная колонка — доля прогревов с капчей:
  // именно она показывает, какое устройство или уровень «цепляет» капчу чаще.
  const byIdentity = (src?: Record<string, { profiles?: number; cap_sum?: number; with_cap?: number }>) =>
    Object.entries(src || {})
      .map(([name, r]) => {
        const profiles = Number(r?.profiles) || 0
        const withCap = Number(r?.with_cap) || 0
        return {
          name,
          profiles,
          withCap,
          capSum: Number(r?.cap_sum) || 0,
          capShare: profiles ? (withCap / profiles) * 100 : 0,
        }
      })
      .filter(r => r.profiles > 0)
      .sort((a, b) => b.profiles - a.profiles)

  const byModel = useMemo(() => byIdentity(d?.by_model), [d?.by_model])
  const byLevel = useMemo(
    () => byIdentity(d?.by_level).sort((a, b) => a.name.localeCompare(b.name, 'ru', { numeric: true })),
    [d?.by_level])

  // Средняя доля капчи по всем прогревам — база для подсветки «хуже обычного».
  const avgCapShare = useMemo(() => {
    const p = byModel.reduce((s, r) => s + r.profiles, 0)
    const w = byModel.reduce((s, r) => s + r.withCap, 0)
    return p ? (w / p) * 100 : 0
  }, [byModel])

  const searching = q.trim().length > 0
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleHost = (host: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(host)) next.delete(host)
    else next.add(host)
    return next
  })

  const byType = sortedEntries(d?.by_type)
  const byDiff = sortedEntries(d?.by_difficulty)
  const maxType = byType[0]?.count || 0
  const maxDiff = byDiff[0]?.count || 0
  const maxProxy = hostGroups[0]?.count || 0

  const hotProxyShare = maxProxy > 0 && solved > 0 ? Math.round((maxProxy / solved) * 100) : 0
  const portsTotal = hostGroups.reduce((s, g) => s + g.ports.length, 0)

  return (
    <div className="h-full overflow-y-auto px-3 py-3 sm:px-4 select-text space-y-4">
      {/* Hero */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60 flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-400 shrink-0">
            <Shield className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">Капча и прокси</div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Какие прокси чаще ловят капчу, какой тип и сложность, сколько циклов решений
              уходит на профиль. Данные с потока — в лог не пишутся.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border/50">
          <HeroMetric
            icon={<Shield className="h-3.5 w-3.5" />}
            label="Решено капч"
            value={solved ? solved.toLocaleString('ru-RU') : '—'}
          />
          <HeroMetric
            icon={<Gauge className="h-3.5 w-3.5" />}
            label="Ср. циклов на капчу"
            value={fmtAvg(cyclesSum, solved)}
            hint="Сколько раундов решения в среднем на одну пройденную капчу"
          />
          <HeroMetric
            icon={<Users className="h-3.5 w-3.5" />}
            label="Ср. циклов на профиль"
            value={fmtAvg(profileCapSum, profilesOk)}
            hint="Среднее по успешным итерациям (включая профили без капчи = 0)"
          />
          <HeroMetric
            icon={<Server className="h-3.5 w-3.5" />}
            label="Профили с капчей"
            value={profilesWithCap
              ? `${profilesWithCap.toLocaleString('ru-RU')} · ${fmtPct(profilesWithCap, profilesOk)}`
              : '—'}
            hint="Доля успешных прогревов, где был хотя бы один цикл решения"
          />
        </div>
      </div>

      {!solved && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <Shield className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="text-sm font-medium text-foreground">Пока пусто</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto leading-relaxed">
            После следующей капчи здесь появятся прокси, типы и средние циклы.
            В обычном логе эта телеметрия не показывается.
          </p>
        </div>
      )}

      {solved > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <section className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Типы капчи
            </h3>
            {byType.length === 0 ? (
              <p className="text-xs text-muted-foreground">Нет данных</p>
            ) : (
              <div className="space-y-0.5">
                {byType.map(t => (
                  <BarRow key={t.name} name={t.name} count={t.count} max={maxType} tone="violet" />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Сложность
            </h3>
            {byDiff.length === 0 ? (
              <p className="text-xs text-muted-foreground">Нет данных по сложности</p>
            ) : (
              <div className="space-y-0.5">
                {byDiff.map(t => (
                  <BarRow key={t.name} name={t.name} count={t.count} max={maxDiff} tone="amber" />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {(byModel.length > 0 || byLevel.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <section className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border/60">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Устройства отпечатков
              </h3>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                Доля прогревов, где встретилась капча
                {avgCapShare > 0 ? ` · в среднем ${Math.round(avgCapShare)}%` : ''}
              </p>
            </div>
            {byModel.length === 0 ? (
              <p className="px-4 py-4 text-xs text-muted-foreground">Пока нет данных</p>
            ) : (
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                      <th className="px-4 py-2 font-medium">Устройство</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">Прогревов</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">С капчей</th>
                      <th className="px-4 py-2 font-medium tabular-nums text-right">Ср. циклов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byModel.map(r => {
                      const worse = avgCapShare > 0 && r.profiles >= 5 && r.capShare >= avgCapShare * 1.3
                      return (
                        <tr key={r.name} className={`border-t border-border/50 ${worse ? 'bg-amber-500/5' : ''}`}>
                          <td className="px-4 py-2 font-mono text-[11px] text-foreground truncate max-w-[12rem]" title={r.name}>
                            {r.name}
                          </td>
                          <td className="px-3 py-2 tabular-nums text-right text-muted-foreground">
                            {r.profiles.toLocaleString('ru-RU')}
                          </td>
                          <td className={`px-3 py-2 tabular-nums text-right font-semibold ${worse ? 'text-amber-400' : 'text-violet-300'}`}
                              title={`${r.withCap} из ${r.profiles}`}>
                            {Math.round(r.capShare)}%
                          </td>
                          <td className="px-4 py-2 tabular-nums text-right text-foreground">
                            {fmtAvg(r.capSum, r.profiles)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border/60">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Уровни профиля
              </h3>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                На каком уровне прокачки капча встречается чаще
              </p>
            </div>
            {byLevel.length === 0 ? (
              <p className="px-4 py-4 text-xs text-muted-foreground">Пока нет данных</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                      <th className="px-4 py-2 font-medium">Уровень</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">Прогревов</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">С капчей</th>
                      <th className="px-4 py-2 font-medium tabular-nums text-right">Ср. циклов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byLevel.map(r => (
                      <tr key={r.name} className="border-t border-border/50">
                        <td className="px-4 py-2 text-foreground">L{r.name}</td>
                        <td className="px-3 py-2 tabular-nums text-right text-muted-foreground">
                          {r.profiles.toLocaleString('ru-RU')}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-right font-semibold text-violet-300"
                            title={`${r.withCap} из ${r.profiles}`}>
                          {Math.round(r.capShare)}%
                        </td>
                        <td className="px-4 py-2 tabular-nums text-right text-foreground">
                          {fmtAvg(r.capSum, r.profiles)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {solved > 0 && (
        <section className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-between">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Прокси-серверы
              </h3>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                {hostGroups.length.toLocaleString('ru-RU')} серверов · {portsTotal.toLocaleString('ru-RU')} портов
                {hostGroups[0] && hotProxyShare >= 25 ? (
                  <span className="text-amber-400/90">
                    {' '}· топ {hostGroups[0].host} даёт {hotProxyShare}% капч
                  </span>
                ) : null}
              </p>
            </div>
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Фильтр по IP / порту"
                className="w-full h-8 pl-8 pr-2 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              />
            </div>
          </div>

          {visibleGroups.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              Ничего не найдено по «{q}»
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    <th className="px-4 py-2 font-medium">Сервер</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Портов</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Прокачек</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Капч</th>
                    <th
                      className="px-3 py-2 font-medium tabular-nums text-right"
                      title="Доля прокачек через этот сервер, где встретилась капча"
                    >Капчит</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Ср. циклов</th>
                    <th className="px-4 py-2 font-medium">Типы</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleGroups.map((g, i) => {
                    // Главный вопрос оператора — какой сервер капчит ЧАЩЕ,
                    // поэтому доля считается от прокачек через него, а не от
                    // общего числа капч (там у каждого сервера доли процента).
                    const rate = g.profiles ? Math.round((g.with_cap / g.profiles) * 100) : null
                    const open = searching || expanded.has(g.host)
                    // «Чаще остальных» — только при достаточном объёме, иначе
                    // один неудачный прогрев даёт 100% и уводит внимание.
                    const hot = rate !== null && g.profiles >= 20 && rate >= 60
                    return [
                      <tr
                        key={g.host}
                        onClick={() => toggleHost(g.host)}
                        className={`border-t border-border/50 hover:bg-muted/20 cursor-pointer ${hot ? 'bg-amber-500/5' : ''}`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1.5">
                            {open
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                            <span className="font-mono text-[11px] text-foreground">{g.host}</span>
                          </div>
                          {hot && (
                            <div className="text-[10px] text-amber-400/90 mt-0.5 pl-5">Чаще остальных</div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right text-muted-foreground">
                          {g.ports.length}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right text-muted-foreground">
                          {g.profiles ? g.profiles.toLocaleString('ru-RU') : '—'}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right font-semibold text-violet-300">
                          {g.count.toLocaleString('ru-RU')}
                        </td>
                        <td className={`px-3 py-2.5 tabular-nums text-right font-semibold ${
                          rate === null ? 'text-muted-foreground'
                            : rate >= 60 ? 'text-amber-400'
                            : rate >= 35 ? 'text-foreground'
                            : 'text-emerald-400'}`}>
                          {rate === null ? '—' : `${rate}%`}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right text-foreground">
                          {fmtAvg(g.cycles_sum, g.count)}
                        </td>
                        <td className="px-4 py-2.5">
                          <TypeChips types={g.types} />
                        </td>
                      </tr>,
                      ...(open ? g.ports.map(p => {
                        const pRate = p.profiles ? Math.round((p.with_cap / p.profiles) * 100) : null
                        return (
                          <tr key={p.proxy} className="border-t border-border/30 bg-muted/10">
                            <td className="px-4 py-1.5 pl-10">
                              <span className="font-mono text-[11px] text-muted-foreground">:{p.port || '—'}</span>
                            </td>
                            <td className="px-3 py-1.5" />
                            <td className="px-3 py-1.5 tabular-nums text-right text-muted-foreground/70">
                              {p.profiles ? p.profiles.toLocaleString('ru-RU') : '—'}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums text-right text-violet-300/80">
                              {p.count.toLocaleString('ru-RU')}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums text-right text-muted-foreground/70"
                                title="Доля прокачек через этот порт, где встретилась капча">
                              {pRate === null ? '—' : `${pRate}%`}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums text-right text-muted-foreground">
                              {fmtAvg(p.cycles_sum, p.count)}
                            </td>
                            <td className="px-4 py-1.5">
                              <TypeChips types={p.types} />
                            </td>
                          </tr>
                        )
                      }) : []),
                    ]
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function HeroMetric({
  icon, label, value, hint,
}: {
  icon: ReactNode
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="px-4 py-3.5" title={hint}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        <span className="opacity-70">{icon}</span>
        {label}
      </div>
      <div className="mt-1.5 text-xl font-bold tabular-nums text-foreground leading-none">{value}</div>
    </div>
  )
}
